import React, { useState, useMemo, useRef, useEffect } from 'react';
import { FiChevronDown, FiX } from 'react-icons/fi';

/**
 * Type-ahead country selector. Filters as you type; selection is constrained
 * to the provided list (free text reverts on blur). `countries` may contain
 * "-" separator entries, which are ignored.
 */
const CountryPicker = ({ value, onChange, countries }) => {
  const options = useMemo(
    () => countries.filter(c => c && !c.startsWith('-')),
    [countries]
  );

  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(c => c.toLowerCase().includes(q));
  }, [query, options]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) commitOrRevert(); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  });

  const select = (country) => {
    onChange(country);
    setQuery(country);
    setOpen(false);
  };

  const commitOrRevert = () => {
    const exact = options.find(c => c.toLowerCase() === query.trim().toLowerCase());
    if (exact) select(exact);
    else setQuery(value || '');
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHighlight(h => Math.min(h + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (open && matches[highlight]) select(matches[highlight]); }
    else if (e.key === 'Escape') { setQuery(value || ''); setOpen(false); }
  };

  return (
    <div className="cw-typeahead" ref={wrapRef}>
      <input
        type="text"
        placeholder="Start typing a country…"
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); setHighlight(0); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        autoComplete="off"
      />
      {query ? (
        <button
          type="button" className="cw-typeahead-icon"
          onClick={() => { onChange(''); setQuery(''); setOpen(true); }}
          aria-label="Clear"
        ><FiX size={16} /></button>
      ) : (
        <span className="cw-typeahead-icon cw-typeahead-icon--chevron"><FiChevronDown size={16} /></span>
      )}

      {open && matches.length > 0 && (
        <ul className="cw-typeahead-list">
          {matches.slice(0, 60).map((c, i) => (
            <li
              key={c}
              className={`cw-typeahead-opt${i === highlight ? ' cw-typeahead-opt--hl' : ''}${c === value ? ' cw-typeahead-opt--sel' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); select(c); }}
              onMouseEnter={() => setHighlight(i)}
            >
              {c}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CountryPicker;
